import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Bot, History } from "lucide-react";
import { Alert, Button, ErrorState } from "@/components/ui";
import { useContextApi, ContextUnavailableError, contextErrorMessage, type Version } from "@/lib/contextApi";
import { cn } from "@/lib/utils";
import { CatalogView, type BrowserSelection, type CatalogTab } from "./CatalogView";
import type { CardSection } from "./ContextCard";
import { OrchestratorChat } from "./OrchestratorChat";
import { ProposalsView } from "./ProposalsPanel";
import { VersionPicker, VersionsView } from "./VersionBar";
import { formatDateTime, UnavailableProvider } from "./shared";

type View = "catalogus" | "voorstellen" | "versies";
const VIEWS: { id: View; label: string }[] = [
  { id: "catalogus", label: "Catalogus" },
  { id: "voorstellen", label: "Voorstellen" },
  { id: "versies", label: "Versies" },
];
const SECTIONS: CardSection[] = ["overzicht", "kolommen", "joins", "recepten", "gebruik", "herkomst"];

/**
 * All navigation state lives in the URL (?weergave=&tabel=&begrip=&sectie=&versie=&voorstel=&assistent=1),
 * so every table, section, proposal and historical version is linkable and Back works.
 */
function useUrlState() {
  const [params, setParams] = useSearchParams();
  const view = (VIEWS.some((v) => v.id === params.get("weergave")) ? params.get("weergave") : "catalogus") as View;
  const table = params.get("tabel");
  const concept = params.get("begrip");
  const selection: BrowserSelection = table ? { type: "table", fqn: table } : concept ? { type: "concept", name: concept } : null;
  const sec = params.get("sectie") as CardSection | null;
  const section: CardSection = sec && SECTIONS.includes(sec) ? sec : "overzicht";
  const catalogTab: CatalogTab = params.get("lijst") === "begrippen" || (!table && concept) ? "begrippen" : "tabellen";
  const update = useCallback(
    (patch: Record<string, string | null>, replace = false) => {
      setParams(
        (cur) => {
          const n = new URLSearchParams(cur);
          Object.entries(patch).forEach(([k, v]) => (v === null || v === "" ? n.delete(k) : n.set(k, v)));
          if (n.get("weergave") === "catalogus") n.delete("weergave");
          if (n.get("sectie") === "overzicht") n.delete("sectie");
          if (n.get("lijst") === "tabellen") n.delete("lijst");
          return n;
        },
        { replace },
      );
    },
    [setParams],
  );
  return {
    view,
    selection,
    section,
    catalogTab,
    version: params.get("versie") ?? undefined,
    proposal: params.get("voorstel"),
    chatOpen: params.get("assistent") === "1",
    update,
  };
}

/** "xl" between 1280 and 1600 px (assistant docks but the list rail collapses), "2xl" above, "sm" below. */
function useWidth(): "sm" | "xl" | "2xl" {
  const get = (): "sm" | "xl" | "2xl" => (typeof window === "undefined" ? "2xl" : window.innerWidth >= 1600 ? "2xl" : window.innerWidth >= 1280 ? "xl" : "sm");
  const [w, setW] = useState(get);
  useEffect(() => {
    const on = () => setW(get());
    window.addEventListener("resize", on);
    return () => window.removeEventListener("resize", on);
  }, []);
  return w;
}

export default function DatabaseContextPage() {
  const api = useContextApi();
  const url = useUrlState();
  const [unavailable, setUnavailable] = useState(false);
  const [versions, setVersions] = useState<Version[]>([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [versionsError, setVersionsError] = useState<string | null>(null);
  const [proposalsKey, setProposalsKey] = useState(0);
  const [pending, setPending] = useState<number | null>(null);
  const [mountKey, setMountKey] = useState(0);
  const [chatTable, setChatTable] = useState<string | undefined>(undefined);
  const [chatFocus, setChatFocus] = useState(0);
  const urlRef = useRef(url);
  urlRef.current = url;

  const loadVersions = useCallback(
    async (opts: { resetToLive?: boolean } = {}) => {
      setRefreshing(true);
      try {
        const list = await api.listVersions();
        const vs = Array.isArray(list) ? list : [];
        setVersions(vs);
        setVersionsError(null);
        const cur = urlRef.current.version;
        if (opts.resetToLive || (cur && !vs.some((v) => v.version === cur))) urlRef.current.update({ versie: null }, true);
      } catch (e) {
        if (e instanceof ContextUnavailableError) setUnavailable(true);
        else setVersionsError(contextErrorMessage(e));
      } finally {
        setRefreshing(false);
        setInitialLoading(false);
      }
    },
    [api],
  );

  useEffect(() => {
    void loadVersions();
  }, [loadVersions]);

  // Count of proposals awaiting review for the nav badge.
  useEffect(() => {
    const ac = new AbortController();
    api
      .listProposals("ready", ac.signal)
      .then((d) => setPending(Array.isArray(d) ? d.length : null))
      .catch(() => undefined);
    return () => ac.abort();
  }, [api, proposalsKey]);

  const live = versions.find((v) => v.live) ?? versions[0];
  const viewing = versions.find((v) => v.version === url.version) ?? live;
  const versionParam = viewing && !viewing.live ? viewing.version : undefined;
  const selectedFqn = url.selection?.type === "table" ? url.selection.fqn : undefined;

  // Esc closes the assistant drawer.
  useEffect(() => {
    if (!url.chatOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !document.querySelector('[role="dialog"]') && window.innerWidth < 1280) url.update({ assistent: null });
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [url]);

  const reportUnavailable = useCallback(() => setUnavailable(true), []);
  const retry = () => {
    setUnavailable(false);
    setInitialLoading(true);
    setMountKey((k) => k + 1);
    void loadVersions();
  };
  const onContentChanged = useCallback(() => {
    void loadVersions({ resetToLive: true });
    setProposalsKey((k) => k + 1);
  }, [loadVersions]);
  const onProposalCreated = useCallback(() => setProposalsKey((k) => k + 1), []);

  const openTable = (fqn: string) => url.update({ weergave: null, tabel: fqn, begrip: null, lijst: null });
  const ask = (fqn: string) => {
    setChatTable(fqn);
    setChatFocus((n) => n + 1);
    url.update({ assistent: "1" });
  };
  const contextForChat = chatTable ?? selectedFqn;
  const wide = useWidth();
  useEffect(() => setChatTable(undefined), [selectedFqn]);

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* Toolbar */}
      <div className="flex h-12 shrink-0 items-center gap-2 border-b border-border bg-card px-3 lg:h-14 lg:gap-6 lg:px-6">
        <h1 className="sr-only text-lg font-semibold tracking-tight lg:not-sr-only lg:shrink-0">Databasecontext</h1>
        <nav className="flex h-full min-w-0 flex-1 items-stretch gap-1 overflow-x-auto lg:flex-none" aria-label="Weergave">
          {VIEWS.map((v) => (
            <button
              key={v.id}
              type="button"
              onClick={() => url.update({ weergave: v.id })}
              aria-current={url.view === v.id ? "page" : undefined}
              className={cn(
                "inline-flex shrink-0 items-center gap-1.5 border-b-2 px-3 text-sm font-medium transition-colors",
                url.view === v.id ? "border-sl-yellow text-foreground" : "border-transparent text-muted-foreground hover:text-foreground",
              )}
            >
              {v.label}
              {v.id === "voorstellen" && !!pending && (
                <span className="rounded-full bg-sl-charcoal px-1.5 text-xs font-semibold tabular-nums text-white" title={`${pending} wachten op review`}>
                  {pending}
                </span>
              )}
            </button>
          ))}
        </nav>
        <Button
          variant={url.chatOpen ? "secondary" : "outline"}
          size="icon"
          className="h-10 w-10 shrink-0 lg:hidden"
          onClick={() => url.update({ assistent: url.chatOpen ? null : "1" })}
          aria-pressed={url.chatOpen}
          aria-label="Contextassistent"
        >
          <Bot className="h-4 w-4" />
        </Button>
        <div className="hidden min-w-0 items-center gap-2 lg:ml-auto lg:flex">
          <VersionPicker versions={versions} selected={viewing?.version} onSelect={(v) => url.update({ versie: versions.find((x) => x.version === v)?.live ? null : v })} />
          <Button
            variant={url.chatOpen ? "secondary" : "outline"}
            size="sm"
            className="hidden h-9 lg:inline-flex"
            onClick={() => url.update({ assistent: url.chatOpen ? null : "1" })}
            aria-pressed={url.chatOpen}
            title="Contextassistent openen of sluiten"
          >
            <Bot className="h-4 w-4" />
            Assistent
          </Button>
        </div>
      </div>

      {viewing && !viewing.live && !unavailable && (
        <div className="flex shrink-0 flex-wrap items-center gap-x-3 gap-y-1 border-b border-sl-yellow/60 bg-sl-yellow-soft px-4 py-2 text-sm lg:px-6" role="status">
          <History className="h-4 w-4 shrink-0" />
          <span>
            Je bekijkt een <span className="font-semibold">niet-live versie</span> van {formatDateTime(viewing.updated_at)} ({viewing.note ?? viewing.version}). De agents
            gebruiken de live versie.
          </span>
          <button type="button" className="font-semibold underline underline-offset-2" onClick={() => url.update({ versie: null })}>
            Naar live
          </button>
          <button type="button" className="font-semibold underline underline-offset-2" onClick={() => url.update({ weergave: "versies" })}>
            Vergelijk of maak live
          </button>
        </div>
      )}

      <div className="flex min-h-0 flex-1">
        <main className="flex min-h-0 min-w-0 flex-1 flex-col">
          {unavailable ? (
            <ErrorState
              offline
              title="Contextservice niet bereikbaar"
              message="De kennisservice reageert niet of is (nog) niet gekoppeld aan het dashboard. Je selectie blijft bewaard; probeer het zo opnieuw."
              onRetry={retry}
              className="flex-1"
            />
          ) : initialLoading ? (
            <div className="flex min-h-0 flex-1" role="status" aria-label="Context laden">
              <div className="hidden w-[340px] space-y-3 border-r border-border bg-card p-4 lg:block">
                {Array.from({ length: 10 }).map((_, i) => (
                  <div key={i} className="h-4 animate-pulse rounded bg-muted" />
                ))}
              </div>
              <div className="flex-1 bg-card" />
            </div>
          ) : (
            <UnavailableProvider value={reportUnavailable}>
              <div key={mountKey} className="flex min-h-0 flex-1 flex-col">
                {versionsError && (
                  <Alert tone="danger" className="m-4" onClose={() => setVersionsError(null)}>
                    Versies konden niet worden geladen: {versionsError}
                  </Alert>
                )}
                {url.view === "catalogus" && (
                  <CatalogView
                    version={versionParam}
                    versionInfo={viewing}
                    tab={url.catalogTab}
                    onTab={(t) => url.update({ lijst: t })}
                    selection={url.selection}
                    onSelect={(s) =>
                      url.update(
                        s?.type === "table"
                          ? { tabel: s.fqn, begrip: null }
                          : s?.type === "concept"
                            ? { begrip: s.name, tabel: null, lijst: "begrippen" }
                            : { tabel: null, begrip: null, sectie: null },
                      )
                    }
                    section={url.section}
                    onSection={(s) => url.update({ sectie: s }, true)}
                    onAsk={ask}
                    railCollapsed={url.chatOpen && wide === "xl"}
                  />
                )}
                {url.view === "voorstellen" && (
                  <ProposalsView
                    refreshKey={proposalsKey}
                    openId={url.proposal}
                    onOpen={(id) => url.update({ voorstel: id }, !url.proposal && !!id)}
                    onChanged={onContentChanged}
                    liveVersion={live?.version}
                    onOpenTable={openTable}
                  />
                )}
                {url.view === "versies" && (
                  <VersionsView
                    versions={versions}
                    viewing={viewing?.version}
                    onView={(v) => url.update({ weergave: null, versie: versions.find((x) => x.version === v)?.live ? null : v })}
                    onRefresh={() => loadVersions()}
                    refreshing={refreshing}
                    onOpenTable={openTable}
                  />
                )}
              </div>
            </UnavailableProvider>
          )}
        </main>

        {/* Assistant: docked column from 1280 px (list rail collapses below 1600 px), drawer otherwise. Stays mounted so a running answer survives closing. */}
        {url.chatOpen && <div className="fixed inset-0 z-[880] bg-sl-charcoal/40 xl:hidden" onClick={() => url.update({ assistent: null })} aria-hidden />}
        <aside
          className={cn(
            "z-[890] flex-col border-l border-border bg-card",
            "fixed inset-y-0 right-0 w-full shadow-pop sm:w-[440px]",
            "xl:static xl:z-auto xl:w-[400px] xl:shadow-none 2xl:w-[440px]",
            url.chatOpen ? "flex" : "hidden",
          )}
          aria-label="Contextassistent"
        >
          <UnavailableProvider value={reportUnavailable}>
            <OrchestratorChat
              onProposal={onProposalCreated}
              onOpenProposal={(id) => url.update({ weergave: "voorstellen", voorstel: id })}
              onClose={() => url.update({ assistent: null })}
              contextTable={contextForChat}
              onClearContext={() => setChatTable("")}
              focusNonce={chatFocus}
            />
          </UnavailableProvider>
        </aside>
      </div>
    </div>
  );
}
